/**
 * Helpers for the Apollo error link's network-error branch.
 *
 * Two transport paths reach that branch: the HTTP link (queries/mutations) and
 * the Amplify AppSync subscription link. The subscription link forwards
 * Amplify's plain-object errors unchanged, and Apollo — which only understands
 * Error instances — wraps them in `UnconventionalError`, whose message is the
 * opaque "An error of unexpected shape occurred." Unwrapping that wrapper lets
 * the link classify and report the real cause instead of the wrapper.
 */
import { UnconventionalError } from "@apollo/client/errors"

/**
 * Returns the underlying cause of an `UnconventionalError` (the original
 * non-Error value Apollo wrapped), or the error itself when it is not wrapped.
 * Use this before classifying or describing a network error so subscription
 * errors are seen as their real cause rather than the wrapper.
 */
export const unwrapUnconventionalError = (error: unknown): unknown =>
  UnconventionalError.is(error) ? error.cause : error

const CONNECTION_FAILED_PREFIX = "Connection failed:"

// Causes Amplify's AppSync realtime transport emits during recoverable
// connection churn: AppSync closes the socket when the JWT it was opened with
// expires (UnauthorizedException), re-subscribing after a reconnect races
// AppSync's cleanup of the old subscription id (DuplicatedOperationError), and
// keep-alive loss surfaces as "Timeout disconnect" or no cause at all. Amplify
// errors the subscription's observer for all of them without retrying it, so
// recovery is on us — see the retry in amplifySubscriptionLink.
const TRANSIENT_CONNECTION_CAUSES = [
  "UnauthorizedException",
  "DuplicatedOperationError",
  "Timeout disconnect"
]

/**
 * Returns true when a subscription error message describes a transient AppSync
 * connection failure that a resubscribe cures (each attempt gets a fresh
 * subscription id and re-fetches the auth header). amplifySubscriptionLink
 * uses this to decide whether to retry; anything else — including exhausted
 * retries — propagates to the error link and is recorded to RUM.
 *
 * @param message - the message derived by {@link describeNetworkError}
 */
export const isTransientSubscriptionError = (message: string): boolean => {
  const trimmed = message.trim()
  if (!trimmed.startsWith(CONNECTION_FAILED_PREFIX)) {
    return false
  }
  const cause = trimmed.slice(CONNECTION_FAILED_PREFIX.length).trim()
  return cause === "" || TRANSIENT_CONNECTION_CAUSES.some((known) => cause.includes(known))
}

const safeStringify = (value: unknown, fallback: string): string => {
  try {
    return JSON.stringify(value)
  } catch {
    return fallback
  }
}

/**
 * Extracts a useful RUM message and name from a (possibly non-Error) network
 * error. Amplify subscription errors arrive as plain objects that commonly
 * carry an `errors` array whose entries each have a `message`; this surfaces
 * those messages instead of the generic wrapper text so RUM shows what
 * actually broke.
 *
 * @param error - the unwrapped network error (see {@link unwrapUnconventionalError})
 * @param fallbackMessage - message to use when nothing better can be derived
 */
export const describeNetworkError = (
  error: unknown,
  fallbackMessage: string
): { message: string; name: string } => {
  if (error instanceof Error) {
    return { message: error.message, name: error.name || "NetworkError" }
  }

  if (error && typeof error === "object") {
    const { name, message, errors } = error as {
      name?: string
      message?: string
      errors?: Array<{ message?: string }>
    }
    const derived =
      Array.isArray(errors) && errors.length > 0
        ? errors.map((entry) => entry.message ?? "unknown").join("; ")
        : message ?? safeStringify(error, fallbackMessage)
    return { message: derived, name: name ?? "SubscriptionError" }
  }

  return { message: fallbackMessage, name: "NetworkError" }
}
